import type { Event } from '@/payload-types'

/**
 * Event times are authored and displayed in one fixed zone so a page rendered on
 * a UTC server shows the same clock time an editor typed into the admin UI.
 */
export const EVENT_TIME_ZONE = 'America/New_York'

/** Fallback label shown next to a time when an event doesn't set its own. */
export const DEFAULT_TIME_ZONE_LABEL = 'ET'

export interface PartitionedEvents {
  /** Soonest first — the next thing on the calendar leads the page. */
  upcoming: Event[]
  /** Most recent first. */
  past: Event[]
}

function toTime(value?: string | null): number | null {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * When an event stops being "upcoming": its end date if it has one, otherwise its
 * start date. A multi-day conference stays upcoming until the last day passes.
 */
export function eventEndsAt(event: Pick<Event, 'startDate' | 'endDate'>): number | null {
  return toTime(event.endDate) ?? toTime(event.startDate)
}

export function isUpcoming(
  event: Pick<Event, 'startDate' | 'endDate'>,
  now: number = Date.now(),
): boolean {
  const endsAt = eventEndsAt(event)
  // An event with an unparseable date is treated as upcoming rather than silently
  // dropped into the archive.
  if (endsAt === null) return true
  return endsAt >= now
}

/** Split a listing into upcoming (ascending) and past (descending). */
export function partitionEvents(events: Event[], now: number = Date.now()): PartitionedEvents {
  const upcoming: Event[] = []
  const past: Event[] = []

  for (const event of events) {
    if (isUpcoming(event, now)) {
      upcoming.push(event)
    } else {
      past.push(event)
    }
  }

  upcoming.sort((a, b) => (toTime(a.startDate) ?? 0) - (toTime(b.startDate) ?? 0))
  past.sort((a, b) => (toTime(b.startDate) ?? 0) - (toTime(a.startDate) ?? 0))

  return { upcoming, past }
}

function formatDate(value: string, options: Intl.DateTimeFormatOptions): string {
  return new Date(value).toLocaleDateString('en-US', { timeZone: EVENT_TIME_ZONE, ...options })
}

function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString('en-US', {
    timeZone: EVENT_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
  })
}

function sameDay(start: string, end: string): boolean {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }
  return formatDate(start, options) === formatDate(end, options)
}

/**
 * "March 14, 2026" for a single day, "March 14 – 16, 2026" when an event spans
 * several days.
 */
export function formatEventDate(
  event: Pick<Event, 'startDate' | 'endDate'>,
  options: { short?: boolean } = {},
): string {
  if (!event.startDate) return ''

  const month = options.short ? ('short' as const) : ('long' as const)
  const full: Intl.DateTimeFormatOptions = { month, day: 'numeric', year: 'numeric' }

  if (!event.endDate || sameDay(event.startDate, event.endDate)) {
    return formatDate(event.startDate, full)
  }

  // Drop the repeated year from the first half of a range inside one year:
  // "March 14 – 16, 2026" rather than "March 14, 2026 – March 16, 2026".
  const sameYear =
    formatDate(event.startDate, { year: 'numeric' }) ===
    formatDate(event.endDate, { year: 'numeric' })

  const start = formatDate(event.startDate, sameYear ? { month, day: 'numeric' } : full)
  return `${start} – ${formatDate(event.endDate, full)}`
}

/** "6:00 PM – 8:00 PM ET", or an empty string when the event has no usable time. */
export function formatEventTime(
  event: Pick<Event, 'startDate' | 'endDate' | 'timezoneLabel'>,
): string {
  if (!event.startDate) return ''

  const label = event.timezoneLabel?.trim() || DEFAULT_TIME_ZONE_LABEL
  const start = formatTime(event.startDate)

  // A range only reads as a time range within one day; multi-day events show
  // their span through the date instead.
  if (event.endDate && sameDay(event.startDate, event.endDate)) {
    const end = formatTime(event.endDate)
    if (end !== start) return `${start} – ${end} ${label}`
  }

  return `${start} ${label}`
}

/** Machine-readable datetime for <time> elements and structured data. */
export function eventDateTimeAttribute(value?: string | null): string | undefined {
  if (!value) return undefined
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** One-line location summary used on cards and in the event header. */
export function formatEventLocation(
  event: Pick<Event, 'locationType' | 'venueName' | 'address' | 'virtualPlatform'>,
): string {
  const platform = event.virtualPlatform?.trim()
  const venue = event.venueName?.trim()
  // The address may be a multi-line block; the summary only needs its first line.
  const addressLine = event.address?.trim().split('\n')[0]?.trim()

  if (event.locationType === 'virtual') {
    return platform ? `Online — ${platform}` : 'Online'
  }

  const inPerson = venue || addressLine || ''

  if (event.locationType === 'hybrid') {
    const online = platform ? `Online — ${platform}` : 'Online'
    return inPerson ? `${inPerson} & ${online}` : online
  }

  return inPerson
}

const DEFAULT_ATTENDANCE_LABELS: Record<Event['attendanceType'], string> = {
  hosting: 'We’re hosting',
  attending: 'We’re attending',
  presenting: 'We’re presenting',
  custom: 'We’re taking part',
}

/**
 * Badge text for our role at an event. The "custom" role carries whatever the
 * editor typed; it falls back to a neutral label if they left it blank.
 */
export function attendanceLabel(event: Pick<Event, 'attendanceType' | 'customRoleLabel'>): string {
  if (event.attendanceType === 'custom') {
    return event.customRoleLabel?.trim() || DEFAULT_ATTENDANCE_LABELS.custom
  }
  return DEFAULT_ATTENDANCE_LABELS[event.attendanceType]
}

/** Lead-in for the list of team members on a card, e.g. "Hosted by: Jane, John". */
export function teamMemberPrefix(event: Pick<Event, 'attendanceType'>): string {
  if (event.attendanceType === 'hosting') return 'Hosted by'
  if (event.attendanceType === 'attending') return 'Attending'
  if (event.attendanceType === 'presenting') return 'Presented by'
  return 'With'
}

/** Heading above the team members on an event page. */
export function teamMemberHeading(event: Pick<Event, 'attendanceType'>): string {
  if (event.attendanceType === 'hosting') return 'Your hosts'
  if (event.attendanceType === 'presenting') return 'Your presenters'
  return 'Who you’ll meet'
}

export const LOCATION_TYPE_LABELS: Record<Event['locationType'], string> = {
  'in-person': 'In person',
  virtual: 'Virtual',
  hybrid: 'Hybrid',
}
